import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  BILLING_COMPANY_ID,
  EOM_FOLDER,
  RECONCILIATION_FOLDER,
  vaultDocTypes,
  type VaultDocument,
} from '@accounting/shared';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { humanizeLabel } from '../../lib/formatters';
import {
  useDeleteVaultDocument,
  useRetryExtraction,
  useUpdateVaultDocument,
  useVaultDocuments,
  useVaultUpload,
  type VaultFilters,
} from '../../hooks/useVault';
import { VaultUploadPanel } from '../../components/documents/VaultUploadPanel';
import { VaultTree } from '../../components/documents/VaultTree';

/**
 * Document vault — drop a stack of files, the AI files them by the date printed
 * on each one, and you search them later.
 *
 * Standalone by design: nothing here is attached to a journal entry, a bill or
 * a reconciliation. It is a filing cabinet, not a ledger.
 */
export const Route = createFileRoute('/_auth/documents/')({
  component: DocumentsPage,
});

function DocumentsPage() {
  const companyId = useActiveCompanyId();

  const [search, setSearch] = useState('');
  const [docType, setDocType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  // '' = the loose, date-filed documents you dropped in yourself.
  const [folder, setFolder] = useState<
    '' | typeof RECONCILIATION_FOLDER | typeof EOM_FOLDER | '*'
  >('');

  // Trim before it reaches the query key so " acme" and "acme" share a cache slot.
  const filters: VaultFilters = useMemo(
    () => ({
      q: search.trim() || undefined,
      docType: (docType || undefined) as VaultFilters['docType'],
      from: from || undefined,
      to: to || undefined,
      needsReview: needsReview || undefined,
      folder: folder || undefined,
    }),
    [search, docType, from, to, needsReview, folder],
  );

  const { data, isLoading, error, isFetching } = useVaultDocuments(companyId, filters);
  const upload = useVaultUpload(companyId);
  const update = useUpdateVaultDocument(companyId);
  const remove = useDeleteVaultDocument(companyId);
  const retry = useRetryExtraction(companyId);

  const documents = data?.documents ?? [];
  const groups = data?.groups ?? [];
  const filtering = !!(filters.q || filters.docType || filters.from || filters.to || filters.needsReview);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Documents</h1>
        <p className="muted">No active company. Pick one from the company switcher first.</p>
      </div>
    );
  }

  const confirmDelete = (doc: VaultDocument) => {
    if (confirm(`Permanently delete "${doc.fileName}" from the vault?`)) remove.mutate(doc.id);
  };

  const clearFilters = () => {
    setSearch('');
    setDocType('');
    setFrom('');
    setTo('');
    setNeedsReview(false);
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Documents</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Drop a stack of files. Each one is stored first, then read for the date printed on it and filed
        by year and month. Nothing is ever discarded — a file the AI cannot read is filed under the month
        you uploaded it and flagged for review. Completed reconciliation packages are filed separately,
        under <strong>Reconciliations</strong>.
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {([
          ['', 'My documents', 'Files you dropped in, filed by date'],
          [RECONCILIATION_FOLDER, 'Reconciliations', 'Completed reconciliation packages, filed by statement date'],
          // Only the company that generates these gets the tab; an empty folder
          // button everywhere else is just a dead end.
          ...(companyId === BILLING_COMPANY_ID
            ? ([[EOM_FOLDER, 'EOM Packages', 'Month-end regulatory packets, filed under the month they report']] as const)
            : []),
          ['*', 'Everything', 'Every document regardless of folder'],
        ] as const).map(([value, label, title]) => (
          <button
            key={value}
            type="button"
            className={folder === value ? 'btn-primary' : 'btn-secondary'}
            title={title}
            onClick={() => setFolder(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Uploading is only offered in the loose area. Reconciliations is a
        * generated folder — dropping a file into it would imply the system put
        * it there, which is exactly the confusion the folder exists to avoid. */}
      {folder === '' && (
        <VaultUploadPanel
          items={upload.items}
          busy={upload.busy}
          onFiles={(files) => void upload.upload(files)}
          onClearFinished={upload.clearFinished}
        />
      )}

      <div
        className="card"
        style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search file names, vendors, summaries and document text…"
          style={{ flex: 1, minWidth: 260 }}
        />
        <label style={{ fontSize: 13 }}>
          Type{' '}
          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="">all</option>
            {vaultDocTypes.map((t) => (
              <option key={t} value={t}>
                {humanizeLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ fontSize: 13 }}>
          To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={needsReview} onChange={(e) => setNeedsReview(e.target.checked)} />
          Needs review
        </label>
        {filtering && (
          <button className="btn-secondary" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Could not load the document vault.</p>}

      {!isLoading && documents.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ margin: 0 }}>
            {filtering
              ? 'No documents match this search.'
              : folder === RECONCILIATION_FOLDER
                ? 'No reconciliation packages yet. Completing a reconciliation files one here automatically, named for the statement date.'
                : 'The vault is empty. Drop your first stack of files above.'}
          </p>
        </div>
      )}

      {documents.length > 0 && (
        <>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            {documents.length} document{documents.length === 1 ? '' : 's'}
            {filters.q ? ` matching “${filters.q}”` : ''}
            {isFetching ? ' · refreshing…' : ''}
          </p>
          <VaultTree
            companyId={companyId}
            groups={groups}
            // A search should not leave its hits hidden behind a closed month.
            expandAll={filtering}
            saving={update.isPending || retry.isPending}
            onSave={(id, input) => update.mutate({ id, input })}
            onDelete={confirmDelete}
            onRetry={(id) => retry.mutate(id)}
          />
        </>
      )}
    </div>
  );
}
